/**
 * ApprovalPreviewResult Component
 *
 * Result of the AI approval dry-run — the decision the automation would apply.
 */
import { Badge, Button, Loader } from '@insio/ui-toolkit';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** What an approval dry-run reports — the invoice and requisition previews share this shape. */
export interface ApprovalPreviewOutcome {
  approvalStatus: string;
  reasoning: string;
  confidenceScore?: number | null;
  flags: ReadonlyArray<string>;
}

export interface ApprovalPreviewResultProps {
  isRunning: boolean;
  hasError: boolean;
  result?: ApprovalPreviewOutcome | null;
  /** Closes the panel until the next dry-run. */
  onDismiss: () => void;
  /** Feature translation prefix of the `preview.*` texts; the invoice ones by default. */
  translationPrefix?: string;
  /** Enum namespace translating `approvalStatus` into the document's target status. */
  statusEnum?: string;
  /** One more line under the reasoning — e.g. whom the policy would ask. */
  footnote?: string | null;
}

export function ApprovalPreviewResult({
  isRunning,
  hasError,
  result,
  onDismiss,
  translationPrefix = 'systemSettings.automation.preview',
  statusEnum = 'invoiceStatus',
  footnote = null
}: ApprovalPreviewResultProps) {
  const t = useFeatureTranslation(translationPrefix);
  const tStatus = useEnumTranslation(statusEnum);

  if (!isRunning && !hasError && !result) {
    return null;
  }

  return (
    <div
      data-testid='approval-preview-section'
      className='rounded-xl border border-border-base bg-background-base p-md'
    >
      <div className='flex items-start justify-between gap-sm'>
        <h2 className='text-base font-semibold text-foreground-base'>{t('resultTitle')}</h2>
        <Button
          variant='ghost'
          size='sm'
          icon='xmark'
          onClick={onDismiss}
          aria-label={t('close')}
          testId='approval-preview-dismiss'
        />
      </div>

      {isRunning && <Loader.Section />}
      {hasError && !isRunning && <p className='text-sm text-error-text mt-sm'>{t('error')}</p>}
      {result && !isRunning && !hasError && (
        <div className='mt-sm space-y-sm' data-testid='approval-preview-result'>
          <div className='flex items-center gap-sm flex-wrap'>
            <Badge color={result.approvalStatus === 'APPROVED' ? 'success' : 'warning'} size='md'>
              {tStatus(result.approvalStatus)}
            </Badge>
            {result.confidenceScore != null && (
              <span className='text-sm text-foreground-muted'>
                {t('confidence', { value: Math.round(result.confidenceScore) })}
              </span>
            )}
          </div>
          <p className='text-sm text-foreground-base whitespace-pre-wrap'>{result.reasoning}</p>
          {result.flags.length > 0 && (
            <div className='flex flex-wrap gap-xs'>
              {result.flags.map(flag => (
                <Badge key={flag} color='info' size='sm'>
                  {flag}
                </Badge>
              ))}
            </div>
          )}
          {footnote && <p className='text-sm text-foreground-base'>{footnote}</p>}
          <p className='text-xs text-foreground-muted'>
            {t('wouldSet', { status: tStatus(result.approvalStatus) })}
          </p>
        </div>
      )}
    </div>
  );
}
